import { useEffect, useRef, useState, useCallback } from "react";
import {
  X, Download, Share2, Loader2, Link2, Check,
  Twitter, Linkedin, Facebook, Mail, MessageCircle,
} from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import ShareCard from "@/components/ShareCard";
import { API } from "@/lib/api";

/**
 * Share preview modal — cross-platform PNG export and share.
 *
 * Why this is structured the way it is (mobile is the hard case):
 *
 *   1. The PNG is captured once, *as soon as the modal opens*, into a Blob
 *      held in state. Every later interaction (download / Web Share / save
 *      from the image) reuses that Blob. This is what keeps iOS Safari's
 *      user-gesture chain intact on the Share button — there is zero async
 *      work between the click and `navigator.share()`.
 *
 *   2. We rasterize at a deliberately modest pixelRatio (1.6) on top of a
 *      1080px source. Higher ratios push past iOS Safari's hard canvas
 *      memory limits (~5–10 MB on iPhone) and the capture silently fails.
 *
 *   3. Web fonts are explicitly awaited via `document.fonts.ready` before
 *      capture. Without this, iOS produces a blank or system-font image
 *      because foreignObject snapshots glyphs before the @font-face files
 *      have actually been embedded.
 *
 *   4. We show the captured PNG inline below the live card. On iOS the
 *      anchor[download] attribute is ignored — but a real <img> can be
 *      long-pressed to "Save image". This gives every platform a working
 *      path: Android/desktop tap Download, iOS taps Share OR long-presses.
 */
export default function ShareCardModal({ open, onClose, question, answer }) {
  const cardRef = useRef(null);
  const objectUrlRef = useRef(null);

  const [pngBlob, setPngBlob] = useState(null);
  const [pngUrl, setPngUrl] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [captureError, setCaptureError] = useState(false);

  // Public share URL — created lazily on first platform-share click. Kept
  // separate from the PNG capture pipeline so download/native-share still
  // work if the /api/shares endpoint is unreachable.
  const [publicUrl, setPublicUrl] = useState(null);
  const [creatingShare, setCreatingShare] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const ensurePublicUrl = useCallback(async () => {
    if (publicUrl) return publicUrl;
    setCreatingShare(true);
    try {
      const r = await fetch(`${API}/shares`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ question: question || "", answer: answer || "" }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const full = `${origin}${data.url}`;
      setPublicUrl(full);
      return full;
    } catch (e) {
      toast.error("Couldn't create share link — try Download PNG instead.");
      return null;
    } finally {
      setCreatingShare(false);
    }
  }, [publicUrl, question, answer]);

  // Detect Web Share with files. Some Androids report navigator.share without
  // canShare — we feature-detect both.
  const canWebShareFiles = useCallback((file) => {
    if (typeof navigator === "undefined") return false;
    if (!navigator.share) return false;
    if (!navigator.canShare) return false;
    try {
      return file ? navigator.canShare({ files: [file] }) : true;
    } catch {
      return false;
    }
  }, []);

  // One-shot capture. Returns a Blob (PNG) or throws.
  const captureBlob = useCallback(async () => {
    const node = cardRef.current;
    if (!node) throw new Error("share-card not mounted");

    // Wait for the fonts that the card actually uses. document.fonts.ready
    // resolves once every font face declared in CSS is loaded or failed —
    // this is the correct primitive for "everything in the page can render
    // now". Without it, iOS Safari rasterizes glyph runs with system fonts.
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch {
        /* ignore — proceed best-effort */
      }
    }
    // Two RAFs let the browser commit layout + paint before we snapshot.
    await new Promise((r) => requestAnimationFrame(() => r()));
    await new Promise((r) => requestAnimationFrame(() => r()));

    // html-to-image's toPng → returns a data: URL. We convert to a Blob via
    // fetch() so we can hand a real File to navigator.share and a real
    // object URL to <img>/<a> — both are way more robust than data: URLs
    // on iOS and old Androids.
    const dataUrl = await toPng(node, {
      pixelRatio: 1.6,
      cacheBust: true,
      backgroundColor: "var(--accent)",
      width: node.offsetWidth,
      height: node.offsetHeight,
      // Skip external fonts inside the snapshot — we already wait for fonts.
      skipFonts: false,
    });
    const res = await fetch(dataUrl);
    return await res.blob();
  }, []);

  // Pre-capture as soon as the modal opens. Holding the result lets the
  // Share button stay synchronous.
  useEffect(() => {
    if (!open) return undefined;

    let alive = true;
    setCaptureError(false);
    setCapturing(true);
    setPngBlob(null);
    setPngUrl(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    // Tiny delay so the card has mounted in the DOM before we snapshot.
    const t = setTimeout(async () => {
      try {
        const blob = await captureBlob();
        if (!alive) return;
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPngBlob(blob);
        setPngUrl(url);
      } catch (err) {
        console.error("share capture failed:", err);
        if (alive) setCaptureError(true);
      } finally {
        if (alive) setCapturing(false);
      }
    }, 60);

    return () => {
      alive = false;
      clearTimeout(t);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [open, captureBlob, question, answer]);

  // Click handler for download. Strategy:
  //   - Android / desktop browsers honour <a download> with an object URL.
  //   - iOS Safari ignores download — but it will navigate to the blob URL,
  //     which renders the PNG in the tab, and the user can long-press → Save.
  //   - If the browser is one where window.open is blocked but anchor works,
  //     the anchor click is the primary path; window.open is the explicit
  //     fallback only when anchor.click doesn't produce a download (iOS).
  // We attempt download via anchor first (no awaits before the click — this
  // counts as a synchronous user-gesture activation).
  const onDownload = () => {
    if (!pngBlob || !pngUrl) {
      toast.error("Image not ready yet — try again.");
      return;
    }
    const isIOS =
      typeof navigator !== "undefined" &&
      /iP(ad|hone|od)/i.test(navigator.userAgent || "");

    const filename = `humane-behavior-ai-${Date.now()}.png`;

    // iOS: skip the futile <a download> and open the blob in a new tab.
    // The PNG renders, the user long-presses → Save to Photos.
    if (isIOS) {
      const win = window.open(pngUrl, "_blank");
      if (!win) {
        // Popup blocked — render inline (already done below) and instruct.
        toast.message("Long-press the image below to save", {
          description: "Then choose 'Save to Photos' / 'Add to Photos'.",
        });
      } else {
        toast.success("Long-press the image to save it");
      }
      return;
    }

    // Android Chrome / Firefox / Edge / desktop: anchor[download] works.
    try {
      const link = document.createElement("a");
      link.href = pngUrl;
      link.download = filename;
      link.rel = "noopener";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      // Defer removal so the browser has time to register the click.
      setTimeout(() => {
        if (link.parentNode) link.parentNode.removeChild(link);
      }, 0);
      toast.success("Saved as image");
    } catch (err) {
      console.error(err);
      // Last-ditch fallback: open the blob in a tab.
      window.open(pngUrl, "_blank");
      toast.message("Long-press the image to save");
    }
  };

  // Click handler for share — must remain synchronous up to navigator.share()
  // because iOS demands the call live inside the same user-gesture activation.
  const onShare = async () => {
    if (!pngBlob) {
      toast.error("Image not ready yet — try again.");
      return;
    }
    const file = new File(
      [pngBlob],
      `humane-behavior-ai-${Date.now()}.png`,
      { type: "image/png" }
    );

    if (!canWebShareFiles(file)) {
      // Browser doesn't expose file sharing — fall through to download path.
      onDownload();
      return;
    }

    setShareBusy(true);
    try {
      await navigator.share({
        files: [file],
        title: "Humane Behavior AI",
        text: question || "From Humane Behavior AI",
      });
      // No success toast — iOS pops its own system share sheet and the user
      // gets confirmation from the destination app.
      onClose();
    } catch (err) {
      // AbortError fires when the user dismisses the share sheet — that's
      // not a failure, don't yell at them.
      if (err && err.name !== "AbortError") {
        console.error("share failed:", err);
        toast.error("Sharing didn't work — try Download instead");
      }
    } finally {
      setShareBusy(false);
    }
  };

  if (!open) return null;

  const shareSupported = canWebShareFiles(
    pngBlob
      ? new File([pngBlob], "x.png", { type: "image/png" })
      : null
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-[var(--text)]/85 backdrop-blur-sm"
      data-testid="share-modal"
    >
      {/* Top bar */}
      <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
        <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)]">
          Share preview
        </div>
        <button
          onClick={onClose}
          disabled={shareBusy}
          className="text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors disabled:opacity-40"
          data-testid="share-modal-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Preview area */}
      <div className="flex-1 overflow-y-auto py-6 px-4 flex flex-col items-center gap-6">
        {/* Hidden, full-size card used only as the rasterization source.
            Positioned FAR off-screen (not opacity:0) because html-to-image
            and html2canvas inherit parent opacity when snapshotting via
            foreignObject on some browsers — that caused the downloaded PNG
            to render as a solid-color rectangle with no content. Moving
            the source to left:-20000px keeps it fully opaque and rendered
            at real dimensions while remaining invisible to the user. */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: "-20000px",
            pointerEvents: "none",
            zIndex: -1,
          }}
          aria-hidden="true"
        >
          <ShareCard ref={cardRef} question={question} answer={answer} />
        </div>

        {/* What the user actually sees: the rasterized PNG itself. This is
            the most honest preview ("you save what you see") and it doubles
            as the long-press-to-save target on iOS. */}
        {capturing && (
          <div
            className="flex flex-col items-center gap-3 text-[var(--text-muted)] py-16"
            data-testid="share-modal-capturing"
          >
            <Loader2 size={22} className="animate-spin" />
            <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase">
              Rendering image…
            </div>
          </div>
        )}
        {!capturing && captureError && (
          <div
            className="flex flex-col items-center gap-3 text-[var(--text-muted)] py-16 max-w-sm text-center"
            data-testid="share-modal-error"
          >
            <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)]">
              Couldn&apos;t render image
            </div>
            <div className="font-ui text-sm">
              Your browser blocked the image capture. Try copying the answer
              text instead, or open the site in Chrome / Safari.
            </div>
          </div>
        )}
        {!capturing && !captureError && pngUrl && (
          <img
            src={pngUrl}
            alt="Shareable reply card"
            className="max-w-full h-auto rounded-md shadow-xl select-auto"
            style={{ maxHeight: "70vh" }}
            data-testid="share-modal-image"
            // crossOrigin is irrelevant for blob: URLs, but explicit silences
            // some lint rules and helps when the preview becomes a save target.
          />
        )}
        {!capturing && !captureError && pngUrl && (
          <p
            className="md:hidden font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] text-center max-w-xs"
            data-testid="share-modal-mobile-hint"
          >
            Tip on iPhone: long-press the image above → Save to Photos.
          </p>
        )}
      </div>

      {/* Action bar */}
      <div className="shrink-0 border-t border-[var(--border)] bg-[var(--bg)] px-4 md:px-6 py-4 space-y-3">
        {/* Platform share row — pre-fills the target app with question + link */}
        <div className="flex flex-wrap items-center justify-center gap-2" data-testid="share-platforms">
          <PlatformBtn
            label="X"
            testid="share-x"
            icon={<Twitter size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              const text = encodeURIComponent(`${question || "From Humane Behavior AI"}\n\n— via @HumaneBehaviorAI`);
              window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
            }}
          />
          <PlatformBtn
            label="LinkedIn"
            testid="share-linkedin"
            icon={<Linkedin size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener");
            }}
          />
          <PlatformBtn
            label="WhatsApp"
            testid="share-whatsapp"
            icon={<MessageCircle size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              const text = encodeURIComponent(`${question || ""}\n\n${url}`);
              window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
            }}
          />
          <PlatformBtn
            label="Facebook"
            testid="share-facebook"
            icon={<Facebook size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener");
            }}
          />
          <PlatformBtn
            label="Reddit"
            testid="share-reddit"
            icon={<Share2 size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              const title = encodeURIComponent(question || "From Humane Behavior AI");
              window.open(`https://reddit.com/submit?title=${title}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
            }}
          />
          <PlatformBtn
            label="Telegram"
            testid="share-telegram"
            icon={<Share2 size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              const text = encodeURIComponent(question || "From Humane Behavior AI");
              window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${text}`, "_blank", "noopener");
            }}
          />
          <PlatformBtn
            label="Email"
            testid="share-email"
            icon={<Mail size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              const subject = encodeURIComponent(question || "From Humane Behavior AI");
              const body = encodeURIComponent(`${answer || ""}\n\nRead more: ${url}`);
              window.location.href = `mailto:?subject=${subject}&body=${body}`;
            }}
          />
          <PlatformBtn
            label={copiedLink ? "Copied" : "Copy link"}
            testid="share-copy-link"
            icon={copiedLink ? <Check size={13} /> : <Link2 size={13} />}
            onClick={async () => {
              const url = await ensurePublicUrl();
              if (!url) return;
              try {
                await navigator.clipboard.writeText(url);
                setCopiedLink(true);
                toast.success("Link copied to clipboard.");
                setTimeout(() => setCopiedLink(false), 2500);
              } catch {
                toast.error("Clipboard blocked — long-press the link input instead.");
              }
            }}
          />
        </div>

        {creatingShare && (
          <div className="flex items-center justify-center gap-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]" data-testid="share-creating">
            <Loader2 size={12} className="animate-spin" /> Creating public link…
          </div>
        )}

        {/* Primary actions — image download + native mobile share */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onDownload}
            disabled={capturing || captureError || shareBusy || !pngBlob}
            className="px-5 py-3 bg-[var(--accent)] hover:bg-[var(--accent-soft)] text-[var(--bg)] font-mono-ui text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 transition-colors disabled:opacity-40 rounded"
            data-testid="share-modal-download"
          >
            <Download size={13} />
            {capturing ? "Preparing…" : "Download PNG"}
          </button>
          {shareSupported && (
            <button
              onClick={onShare}
              disabled={capturing || captureError || shareBusy || !pngBlob}
              className="px-5 py-3 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] text-[var(--text)] font-mono-ui text-[11px] tracking-[0.24em] uppercase inline-flex items-center gap-2 transition-colors disabled:opacity-40 rounded"
              data-testid="share-modal-share"
            >
              <Share2 size={13} />
              {shareBusy ? "Sharing…" : "Share image"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PlatformBtn({ label, icon, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] text-[var(--text)] font-mono-ui text-[10px] tracking-[0.22em] uppercase transition-colors rounded"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
